/**
 * Roles and role groups API.
 */

import { apiClient } from './api-client';
import type {
  Role,
  RoleGroup,
  CreateRolePayload,
  UpdateRolePayload,
  CreateRoleGroupPayload,
  UpdateRoleGroupPayload,
} from '@/types/role.types';
import type { PaginatedResponse } from '@/types/data-table.types';
import { useRoleCatalogStore } from '@/stores/roleCatalogStore';

/** `GET /roles` — paginated role list. */
export const getRoles = async (params?: {
  page?: number;
  pageSize?: number;
  roleGroups?: string[];
  statuses?: string[];
  modes?: string[];
  search?: string;
  minHierarchyLevel?: number;
  maxHierarchyLevel?: number;
  roleGroup?: string;
}): Promise<PaginatedResponse<Role>> => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.roleGroups?.length) searchParams.set('roleGroups', params.roleGroups.join(','));
  if (params?.statuses?.length) searchParams.set('statuses', params.statuses.join(','));
  if (params?.modes?.length) searchParams.set('modes', params.modes.join(','));
  if (params?.search) searchParams.set('search', params.search);
  if (params?.minHierarchyLevel !== undefined)
    searchParams.set('minHierarchyLevel', params.minHierarchyLevel.toString());
  if (params?.maxHierarchyLevel !== undefined)
    searchParams.set('maxHierarchyLevel', params.maxHierarchyLevel.toString());
  if (params?.roleGroup) searchParams.set('roleGroup', params.roleGroup);

  const { data } = await apiClient.get<PaginatedResponse<Role>>(
    `/roles?${searchParams.toString()}`,
  );
  return data;
};

/** `GET /roles/:id` */
export const getRole = async (id: string): Promise<Role> => {
  const { data } = await apiClient.get<Role>(`/roles/${id}`);
  return data;
};

/** `GET /roles` — unwraps paginated or array payloads for pickers. Seeds the login catalog. */
export const fetchRoles = async (): Promise<Role[]> => {
  const { data } = await apiClient.get<Role[] | PaginatedResponse<Role>>('/roles');
  let roles: Role[] = [];
  if (Array.isArray(data)) roles = data;
  else if (data && Array.isArray(data.data)) roles = data.data;
  useRoleCatalogStore.getState().setRoles(roles);
  return roles;
};

/** `POST /roles` */
export const createRole = async (payload: CreateRolePayload): Promise<Role> => {
  const { data } = await apiClient.post<Role>('/roles', payload);
  return data;
};

/** `PATCH /roles/:id` */
export const updateRole = async (id: string, payload: UpdateRolePayload): Promise<Role> => {
  const { data } = await apiClient.patch<Role>(`/roles/${id}`, payload);
  return data;
};

/** `DELETE /roles/:id` */
export const deleteRole = async (id: string): Promise<void> => {
  await apiClient.delete(`/roles/${id}`);
};

/** `GET /roles/groups` */
export const fetchRoleGroups = async (): Promise<RoleGroup[]> => {
  const { data } = await apiClient.get<RoleGroup[]>('/roles/groups');
  return data;
};

/** `POST /roles/groups` */
export const createRoleGroup = async (payload: CreateRoleGroupPayload): Promise<RoleGroup> => {
  const { data } = await apiClient.post<RoleGroup>('/roles/groups', payload);
  return data;
};

/** `PATCH /roles/groups/:id` */
export const updateRoleGroup = async (
  id: string,
  payload: UpdateRoleGroupPayload,
): Promise<RoleGroup> => {
  const { data } = await apiClient.patch<RoleGroup>(`/roles/groups/${id}`, payload);
  return data;
};

/** `DELETE /roles/groups/:id` */
export const deleteRoleGroup = async (id: string): Promise<void> => {
  await apiClient.delete(`/roles/groups/${id}`);
};

/** Trigger a browser download for an XLSX blob. */
const triggerBlobDownload = (data: Blob, filename: string) => {
  const url = window.URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/** `GET /roles/export/excel` — download XLSX. */
export const exportRolesExcel = async (): Promise<void> => {
  const { data } = await apiClient.get('/roles/export/excel', {
    responseType: 'blob',
  });
  const blob = new Blob([data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const timestamp = new Date().toISOString().split('T')[0];
  triggerBlobDownload(blob, `roles-${timestamp}.xlsx`);
};
